import { useQuery, useQueryClient } from "@tanstack/react-query"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BaseInputField from "../../../components/elements/BaseInputField"
import BaseSection from "../../../components/elements/BaseSection"
import BaseSectionHeadline from "../../../components/elements/BaseSectionHeadline"
import BasePage from "../../../components/layout/BasePage"
import { getCompanyData, getLogo } from "../api/apiCalls"
import { Form } from "antd"
import { useEffect, useState } from "react"
import { useUpdateCompanyData, useUploadLogo } from "../api/services"
import { HexColorPicker } from "react-colorful"
import BaseImageUpload from "../../../components/elements/BaseImageUpload"
import { GeneralQueryKeys } from "../../../api/constants"
import ErrorPage from "../../404/ErrorPage"
import Loading from "../../../components/common/Loading"

export default function SettingsPage() {
    const {
        data: companyData,
        isLoading: companyDataLoading,
        isError: companyDataError,
    } = useQuery({
        queryKey: [GeneralQueryKeys.COMPANY_DATA],
        queryFn: getCompanyData,
    })

    const [form] = Form.useForm()

    useEffect(() => {
        if (!companyData) return
        // seed: take the commercial register number from the tax authority text (e.g. "HRB 41540 Amtsgericht Dresden")
        const registerFromTaxAuthority = companyData.tax_authority?.match(/(HR[AB])\s*(\d+)/)
        form.setFieldsValue({
            ...companyData,
            commercial_register: companyData.commercial_register || (registerFromTaxAuthority ? `${registerFromTaxAuthority[1]} ${registerFromTaxAuthority[2]}` : ""),
        })
    }, [companyData])

    const queryClient = useQueryClient()
    const { mutate: updateCompanyData } = useUpdateCompanyData(queryClient, "companyData")

    const handleSubmit = (values: any) => {
        updateCompanyData(values)
    }

    //  display logo
    const [loading, setLoading] = useState(false)
    const { data: logo } = useQuery({
        queryFn: getLogo,
        queryKey: [GeneralQueryKeys.COMPANY_DATA_LOGO],
        enabled: !!companyData?.logo?.s3Key,
    })
    // Upload Logo
    const { mutate: addImage } = useUploadLogo(queryClient, "logo", setLoading)

    if (companyDataLoading) return <Loading />
    if (companyDataError) return <ErrorPage />

    return (
        <BasePage classProps="gap-4">
            <BaseHeadBanner title="Einstellungen" button={<button onClick={() => form.submit()}>Speichern</button>} />
            <BaseGrowBody classProps="flex gap-4">
                <Form form={form} onFinish={handleSubmit} className="w-full flex gap-4">
                    <BaseSection>
                        <BaseSectionHeadline label="Unternehmensdaten" />
                        <Form.Item name="company" rules={[{ required: true, message: "Unternehmensname ist erforderlich" }]} className="mb-2">
                            <BaseInputField label="Unternehmensname *" type="text" onChange={(e) => form.setFieldValue("company", e)} />
                        </Form.Item>
                        <Form.Item name="ceo" rules={[{ required: true, message: "Geschäftsführer ist erforderlich" }]} className="mb-2">
                            <BaseInputField label="Geschäftsführer *" type="text" onChange={(e) => form.setFieldValue("ceo", e)} />
                        </Form.Item>
                        <div className="flex gap-4 w-full">
                            <Form.Item name={["address", "street"]} rules={[{ required: true, message: "Straße ist erforderlich" }]} className="mb-2 w-full">
                                <BaseInputField label="Straße *" type="text" onChange={(e) => form.setFieldValue(["address", "street"], e)} />
                            </Form.Item>
                            <Form.Item name={["address", "nr"]} rules={[{ required: true, message: "Nummer ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="Nummer *" type="text" onChange={(e) => form.setFieldValue(["address", "nr"], e)} classProps="!w-64" />
                            </Form.Item>
                        </div>
                        <div className="flex gap-4 w-full">
                            <Form.Item name={["address", "plz"]} rules={[{ required: true, message: "PLZ ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="PLZ *" type="text" onChange={(e) => form.setFieldValue(["address", "plz"], e)} classProps="!w-64" />
                            </Form.Item>
                            <Form.Item name={["address", "city"]} rules={[{ required: true, message: "Ort ist erforderlich" }]} className="mb-2 w-full">
                                <BaseInputField label="Ort *" type="text" onChange={(e) => form.setFieldValue(["address", "city"], e)} />
                            </Form.Item>
                        </div>
                        <Form.Item name="phone" rules={[{ required: true, message: "Telefon ist erforderlich" }]} className="mb-2">
                            <BaseInputField label="Telefon *" type="text" onChange={(e) => form.setFieldValue("phone", e)} />
                        </Form.Item>
                        <Form.Item name="email" rules={[{ required: true, message: "E-Mail ist erforderlich" }]} className="mb-2">
                            <BaseInputField label="E-Mail *" type="text" onChange={(e) => form.setFieldValue("email", e)} />
                        </Form.Item>
                        <Form.Item name="web" rules={[{ required: true, message: "Website ist erforderlich" }]} className="mb-2">
                            <BaseInputField label="Website *" type="text" onChange={(e) => form.setFieldValue("web", e)} />
                        </Form.Item>
                        <Form.Item name="datenschutz" rules={[{ required: true, message: "Datenschutz ist erforderlich" }]} className="mb-8">
                            <BaseInputField label="Datenschutz *" type="text" onChange={(e) => form.setFieldValue("datenschutz", e)} />
                        </Form.Item>
                        <BaseSectionHeadline label="Stichwörter (max. 5)" />
                        <div className="flex flex-col gap-2">
                            {[0, 1, 2, 3, 4].map((index) => (
                                <Form.Item key={index} name={["keywords", index]} className="!mb-0">
                                    <BaseInputField
                                        label={``}
                                        type="text"
                                        onChange={(e) => {
                                            const currentKeywords = form.getFieldValue("keywords") || []
                                            currentKeywords[index] = e
                                            form.setFieldValue("keywords", currentKeywords)
                                        }}
                                    />
                                </Form.Item>
                            ))}
                        </div>
                    </BaseSection>
                    <div className="flex flex-col gap-4 w-full">
                        <BaseSection classProps="h-96">
                            <BaseSectionHeadline label="Logo & Farben" />
                            <div className="flex gap-4 items-center h-full">
                                {!companyData ? (
                                    <p className="text-xs text-gray-500 px-8 leading-4 text-center py-16 border rounded-default">
                                        Ein Logo kann hochgeladen werden, sobald die Informationen zum Unternehmen eingetragen wurden.
                                    </p>
                                ) : (
                                    <BaseImageUpload classProps="!h-48 !w-full" data={logo ?? ""} onUpload={(formData, done) => addImage({ logo: formData }, done)} loading={loading} setLoading={setLoading} />
                                )}
                                <Form.Item name="color_code" rules={[{ required: true, message: "Farbe ist erforderlich" }]} className="mb-2">
                                    <HexColorPicker
                                        color={companyData?.color_code}
                                        onChange={(e: string) => {
                                            form.setFieldValue("color_code", e)
                                        }}
                                    />
                                </Form.Item>
                            </div>
                        </BaseSection>
                        <BaseSection>
                            <BaseSectionHeadline label="Informationen für die Rechnung" />
                            <Form.Item name="banking_institution" rules={[{ required: true, message: "Bank ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="Bank *" type="text" onChange={(e) => form.setFieldValue("banking_institution", e)} />
                            </Form.Item>
                            <Form.Item name="banking_institution_id" rules={[{ required: true, message: "BLZ ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="BLZ *" type="text" onChange={(e) => form.setFieldValue("banking_institution_id", e)} />
                            </Form.Item>
                            <Form.Item name="banking_bic" rules={[{ required: true, message: "BIC ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="BIC *" type="text" onChange={(e) => form.setFieldValue("banking_bic", e)} />
                            </Form.Item>
                            <Form.Item name="banking_account" rules={[{ required: true, message: "IBAN ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="IBAN *" type="text" onChange={(e) => form.setFieldValue("banking_account", e)} />
                            </Form.Item>
                            <Form.Item name="vat_id" rules={[{ required: true, message: "Umsatzsteuer-ID ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="Umsatzsteuer-ID *" type="text" onChange={(e) => form.setFieldValue("vat_id", e)} />
                            </Form.Item>
                            <Form.Item name="tax_id" rules={[{ required: true, message: "Steuer-ID ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="Steuer-ID *" type="text" onChange={(e) => form.setFieldValue("tax_id", e)} />
                            </Form.Item>
                            <Form.Item name="tax_authority" rules={[{ required: true, message: "Finanzamt ist erforderlich" }]} className="mb-2">
                                <BaseInputField label="Finanzamt *" type="text" onChange={(e) => form.setFieldValue("tax_authority", e)} />
                            </Form.Item>
                            <Form.Item name="commercial_register" className="mb-2">
                                <BaseInputField label="Handelsregisternummer (E-Rechnung, z. B. HRB 41540)" type="text" onChange={(e) => form.setFieldValue("commercial_register", e)} />
                            </Form.Item>
                        </BaseSection>
                    </div>
                </Form>
            </BaseGrowBody>
        </BasePage>
    )
}
